import { type Accessor, createSignal, onMount } from "solid-js";

/**
 * Red de seguridad contra el "flush diferido" del router al cargar una URL
 * profunda (deep-link).
 *
 * El problema: TanStack Router parsea `location.search` de forma asíncrona en su
 * primer ciclo. Si un efecto del componente de ruta lee `useSearch()` y escribe
 * de vuelta a la URL (con `navigate({ replace: true })`) ANTES de que ese primer
 * flush haya asentado los params iniciales, puede pisar el `?order=...` /
 * `?category=...` que venía en el enlace pegado → el deep-link se "pierde" y la
 * vista arranca en su estado por defecto en lugar de en el estado compartido.
 *
 * La defensa: capturamos `location.search` de forma SÍNCRONA en el primer tick
 * (antes de cualquier navegación programática) y lo exponemos como una fuente de
 * verdad estable. El componente de ruta puede preferir este valor capturado
 * sobre `useSearch()` mientras el router todavía no lo refleja, y así sembrar su
 * estado local con lo que realmente venía en la URL. Es idempotente y de solo
 * lectura: nunca manipula el history.
 */

function parseSearch(raw: string): URLSearchParams {
  try {
    return new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  } catch {
    return new URLSearchParams();
  }
}

/**
 * Devuelve un accessor con un único `param` de la `location.search` capturada de
 * forma síncrona en el montaje. `undefined` si no venía en la URL inicial.
 *
 * Uso típico: sembrar un signal de estado (orden activa, categoría, filtro) con
 * el valor del deep-link, sin depender de que el primer flush del router ya haya
 * asentado los search params.
 */
export function createInitialSearchParam(param: string): Accessor<string | undefined> {
  // Captura síncrona en el primer tick del cliente. En SSR/sin `window`
  // devolvemos vacío (el estado se reconstruye en cliente).
  const captureNow = (): string | undefined => {
    const loc = globalThis.location;
    if (!loc) {
      return undefined;
    }
    const value = parseSearch(loc.search).get(param);
    return value && value.trim() !== "" ? value : undefined;
  };

  const [initial, setInitial] = createSignal<string | undefined>(captureNow());

  // Reafirmamos tras el montaje por si la captura síncrona corrió antes de que
  // `location` estuviera disponible (hidratación). No sobrescribe un valor ya
  // capturado con `undefined`: la URL inicial es la verdad.
  onMount(() => {
    const captured = captureNow();
    if (captured !== undefined) {
      setInitial(captured);
    }
  });

  return initial;
}
